import axios from "axios";
import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import {
  getAuthCookies,
  clearAuthCookies,
} from "../auth-cookies/auth-cookies.service";
import { isTokenValid } from "../jwt/jwt.service";
import { getAuthConfig } from "../auth-config/auth-config.service";
import { refreshAuthTokens } from "../token-refresh/token-refresh.service";

const redirectToLogin = (): void => {
  const config = getAuthConfig();
  clearAuthCookies();

  if (globalThis.location !== undefined) {
    const returnUrl = encodeURIComponent(globalThis.location.href);
    globalThis.location.href = `${config.loginUrl}?r=${returnUrl}`;
  }
};

const requestInterceptor = async (
  requestConfig: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
  const cookies = getAuthCookies();
  const { idToken, refreshToken } = cookies;

  if (isTokenValid(idToken)) {
    requestConfig.headers.Authorization = idToken;
    return requestConfig;
  }

  if (!refreshToken) {
    redirectToLogin();
    throw new Error("No refresh token available");
  }

  try {
    const newIdToken = await refreshAuthTokens();
    requestConfig.headers.Authorization = newIdToken;
    return requestConfig;
  } catch (error) {
    redirectToLogin();
    throw error;
  }
};

const createAuthHttpClient = (): AxiosInstance => {
  const instance = axios.create();

  instance.interceptors.request.use(requestInterceptor, (error) => {
    throw error;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        redirectToLogin();
      }
      throw error;
    },
  );

  return instance;
};

export const authHttpClient = createAuthHttpClient();
